import api from '../config/axios'; // Import the configured Axios instance

/**
 * Formatta la richiesta API per operazioni CRUD standard
 * @param {string} tableName - Nome della tabella
 * @param {string} operation - Tipo di operazione ('read', 'create', 'update', 'delete')
 * @param {object} data - Dati da inviare (filters for read, record data for write)
 * @param {string} primaryKey - Chiave primaria per update/delete
 * @returns {object} Payload formattato per la richiesta
 */
export const formatPayload = (tableName, operation, data, primaryKey = null) => {
  if (operation === 'read') {
    // For read, data usually contains filters, pagination, sort info
    return {
      tableName,
      operation,
      ...data // Spread filters, limit, offset, sortBy etc.
    };
  }

  if (operation === 'create') {
    // Backend expects changes array
    return {
      tableName,
      changes: [
        {
          operation,
          ...data // The new record data
        }
      ]
    };
  }

  if (operation === 'update' || operation === 'delete') {
    if (!primaryKey || data[primaryKey] === undefined || data[primaryKey] === null) {
      console.error(`Operazione ${operation} per ${tableName} richiede una chiave primaria valida. Dati ricevuti:`, data);
      throw new Error(`Operazione ${operation} richiede una chiave primaria valida.`);
    }

    // Backend expects changes array
    return {
      tableName,
      changes: [
        {
          operation,
          [primaryKey]: data[primaryKey],
          // Include the rest of the data only for update
          ...(operation === 'update' ? data : {})
        }
      ]
    };
  }

  throw new Error(`Operazione '${operation}' non supportata`);
};

/**
 * Estrae i record da una risposta API, gestendo diversi formati possibili
 * @param {object} response - Oggetto della risposta Axios
 * @returns {array} Array di record
 */
export const extractRecords = (response) => {
  const responseData = response?.data;
  if (!responseData) return [];

  // Check common patterns
  if (Array.isArray(responseData.records)) {
    return responseData.records;
  }
  if (Array.isArray(responseData.data?.records)) {
    return responseData.data.records;
  }
  if (Array.isArray(responseData.data)) {
    return responseData.data;
  }
   if (Array.isArray(responseData)) {
    return responseData;
  }

  console.warn("extractRecords: Impossibile estrarre i record dalla risposta:", response);
  return [];
};

/**
 * Esegue una chiamata API POST con gestione errori standard
 * @param {string} apiEndpoint - Endpoint API (e.g., '/api/crud')
 * @param {object} payload - Dati da inviare
 * @returns {Promise<object>} La risposta dell'API in caso di successo
 * @throws {Error} Errore formattato in caso di fallimento
 */
export const performApiCall = async (apiEndpoint, payload) => {
  try {
    // Usa l'istanza axios importata
    const response = await api.post(apiEndpoint, payload);

    // Check for backend-specific success/error flags if they exist
    if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Errore restituito dal backend.');
    }

    return response; // Return the full Axios response object

  } catch (error) {
    // Rethrow a formatted error
    throw new Error(formatErrorMessage(error));
  }
  // No finally block needed here as we let TableContext handle loading state
};

/**
 * Formatta un messaggio di errore da un oggetto errore (Axios o generico)
 * @param {Error|object|string} error - L'errore da formattare
 * @returns {string} Messaggio di errore leggibile
 */
export const formatErrorMessage = (error) => {
  if (!error) return 'Errore sconosciuto';

  // Axios error structure
  if (error.response) {
    // Error from backend response data
    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        return error.response.data;
      }
      if (error.response.data.message) {
        return error.response.data.message;
      }
       if (error.response.data.error) {
         // Handle nested errors if backend sends { error: { message: '...' } }
         if (typeof error.response.data.error === 'object' && error.response.data.error.message) {
            return error.response.data.error.message;
         }
         return String(error.response.data.error);
      }
    }
    // Fallback to HTTP status text
    return `Errore ${error.response.status}: ${error.response.statusText}`;
  }

  // Network error or request setup error
  if (error.request) {
    return 'Errore di rete o server non raggiungibile.';
  }

  // Standard JavaScript error
  if (error.message) {
    return error.message;
  }

  // Fallback for other types
  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch (e) {
    return 'Errore non serializzabile.';
  }
};